import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import '@xterm/xterm/css/xterm.css';

function normalizeToXterm(text) {
  if (!text) return '';
  const normalized = text.replace(/\r?\n/g, '\r\n');
  return normalized.endsWith('\r\n') ? normalized : `${normalized}\r\n`;
}

function safeDispose(termRef, fitAddonRef, resizeObserverRef) {
  try {
    if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
  } catch (e) {
    // best-effort
  } finally {
    resizeObserverRef.current = null;
  }

  try {
    if (termRef.current) termRef.current.dispose();
  } catch (e) {
    // best-effort
  } finally {
    termRef.current = null;
    fitAddonRef.current = null;
  }
}

export default function XtermLogViewer({
  content,
  follow,
  height,
  background,
  fontSize
}) {
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const fitAddonRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const renderedContentRef = useRef('');

  const normalizedContent = useMemo(() => normalizeToXterm(content), [content]);

  // Create terminal (client-only / SSR-safe).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!containerRef.current) return undefined;

    let isCancelled = false;

    (async () => {
      try {
        const [{ Terminal }, { FitAddon }] = await Promise.all([
          import('@xterm/xterm'),
          import('@xterm/addon-fit')
        ]);

        if (isCancelled || !containerRef.current) return;

        const term = new Terminal({
          convertEol: true,
          disableStdin: true,
          cursorBlink: false,
          scrollback: 5000,
          fontFamily: 'monospace',
          fontSize,
          theme: {
            background
          }
        });

        const fitAddon = new FitAddon();
        term.loadAddon(fitAddon);

        term.open(containerRef.current);
        fitAddon.fit();

        termRef.current = term;
        fitAddonRef.current = fitAddon;

        // Keep fitted on container resize.
        if (typeof ResizeObserver !== 'undefined') {
          const ro = new ResizeObserver(() => {
            try {
              fitAddon.fit();
            } catch (e) {
              // best-effort
            }
          });
          ro.observe(containerRef.current);
          resizeObserverRef.current = ro;
        }

        // Initial render
        renderedContentRef.current = normalizedContent;
        if (normalizedContent) {
          term.write(normalizedContent, () => {
            if (follow) term.scrollToBottom();
          });
        } else if (follow) {
          term.scrollToBottom();
        }
      } catch (e) {
        // best-effort: if xterm fails to load, we just render an empty box.
      }
    })();

    return () => {
      isCancelled = true;
      safeDispose(termRef, fitAddonRef, resizeObserverRef);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update terminal content.
  useEffect(() => {
    const term = termRef.current;
    if (!term) return;

    if (renderedContentRef.current === normalizedContent) {
      if (follow) term.scrollToBottom();
      return;
    }

    renderedContentRef.current = normalizedContent;

    try {
      term.reset();
    } catch (e) {
      // best-effort
    }

    if (normalizedContent) {
      term.write(normalizedContent, () => {
        if (follow) term.scrollToBottom();
      });
    } else if (follow) {
      term.scrollToBottom();
    }
  }, [normalizedContent, follow]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height,
        background,
        borderRadius: 8,
        overflow: 'hidden'
      }}
    />
  );
}

XtermLogViewer.propTypes = {
  content: PropTypes.string,
  follow: PropTypes.bool,
  height: PropTypes.number,
  background: PropTypes.string,
  fontSize: PropTypes.number
};

XtermLogViewer.defaultProps = {
  content: '',
  follow: true,
  height: 420,
  background: '#111827',
  fontSize: 13
};

